import { useGetDepositFormParams } from "../../data-access/useGetDepositFormParams";
import { Box, Skeleton, Typography } from "@mui/material";
import EuroWrapper from "../../../shared/components/EuroWrapper";
import { FormContainer, TextFieldElement, useForm } from "react-hook-form-mui";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSubmitDeposit } from "../../data-access/useSubmitDeposit";
import { LoadingButton } from "@mui/lab";
import PaymentCheckbox from "src/violationCase/components/PaymentCheckbox";

type TDepositFormProps = {
  mode: string;
  appId?: string | null;
  subscriptionStart?: string;
  finePaymentId?: number;
};

function DepositForm(props: TDepositFormProps) {
  const { mode, appId, subscriptionStart, finePaymentId } = props;
  const { t } = useTranslation();
  let depositMode:
    | "certificate"
    | "subscription"
    | "application"
    | "violationCase";
  if (mode === "app") {
    depositMode = "application";
  } else if (mode === "cert") {
    depositMode = "certificate";
  } else if (mode === "violationCase") {
    depositMode = "violationCase";
  } else if (mode === "subscription") {
    depositMode = "subscription";
  }
  const { data, isLoading } = useGetDepositFormParams(
    depositMode,
    appId,
    subscriptionStart,
    finePaymentId,
  );
  const [paymentAccepted, setPaymentAccepted] = useState(false);
  const { mutate, isLoading: isLoadingMutation } =
    useSubmitDeposit(depositMode);
  const validationSchema = yup.object().shape({
    afm: yup
      .string()
      .matches(/^\d{9}$/, t("Το πεδίο πρέπει να αποτελείται μόνο απο ψηφία"))
      .required(t("Το πεδίο είναι υποχρεωτικό")),
    depositCode: yup
      .string()
      .trim()
      .matches(/^\d{20}$/, t("Το πεδίο πρέπει να αποτελείται μόνο απο ψηφία"))
      .required(t("Το πεδίο είναι υποχρεωτικό")),
  });
  const formContext = useForm({
    resolver: yupResolver(validationSchema),
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        maxWidth: { xs: "100%", sm: "90%" },
        mx: "auto",
      }}
    >
      {isLoading ? (
        <Skeleton variant="rectangular" width={50} />
      ) : (
        <>
          <Typography variant="h6">
            {t(
              "Θα πρέπει να χρησιμοποιήσετε ένα ηλεκτρονικό παράβολο αξίας ακριβώς",
            )}{" "}
            {<EuroWrapper amount={data?.data?.amount} />}{" "}
            {t(
              " (που έχετε πληρώσει π.χ. μέσω web banking) και έπειτα να εισάγετε τον κωδικό του στο παρακάτω πεδίο. Στην συνέχεια σε επικοινωνία με την ΓΓΠΣ θα αξιοποιηθεί το παράβολο αυτό ώστε να ολοκληρωθεί η",
            )}{" "}
            {data?.data?.m06_id ? t("καταβολή του τέλους") : t("αίτηση")}
          </Typography>
          {(import.meta.env.DEV || import.meta.env.MODE === "azureDev") && (
            <>
              <Typography>
                (προσοχή: οι συναλλαγές είναι ΕΙΚΟΝΙΚΕΣ αφού το σύστημα είναι σε
                ΤΕΣΤ ΠΕΡΙΒΑΛΛΟΝ): 00516128795412156172 00516130695412157718
              </Typography>
            </>
          )}
          <FormContainer
            formContext={formContext}
            onSuccess={(data) => {
              let depositMode:
                | "certificateApp"
                | "subscription"
                | "application"
                | "violationCase";
              if (mode === "app") {
                depositMode = "application";
              } else if (mode === "cert") {
                depositMode = "certificateApp";
              } else if (mode === "violationCase") {
                depositMode = "violationCase";
              } else if (mode === "subscription") {
                depositMode = "subscription";
              }
              mutate({
                afm: data.afm.trim(),
                depositCode: data.depositCode.trim(),
                mode: depositMode,
              });
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                gap: "1rem",
              }}
            >
              <TextFieldElement name="afm" label={t("Το ΑΦΜ σας")} />
              <TextFieldElement
                name="depositCode"
                label={t("Κωδικός Παραβόλου")}
              />
            </Box>
            {mode === "violationCase" && (
              <PaymentCheckbox
                checked={paymentAccepted}
                onCheckedChange={setPaymentAccepted}
              />
            )}
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{ marginTop: "1rem" }}
              loading={isLoadingMutation}
              disabled={mode === "violationCase" && !paymentAccepted}
            >
              {t("Συνέχεια με επικοινωνία στην ΓΓΠΣ")}
            </LoadingButton>
          </FormContainer>
        </>
      )}
    </Box>
  );
}

export default DepositForm;
